import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import FontAwesomeIcon from '@fortawesome/react-fontawesome'
import {
  faEnvelope,
  faArrowRight,
  faSearch,
  faUser
} from '@fortawesome/fontawesome-free-solid'

import Api from '../../services/api'
import { BASE_URL } from '../../config'
import { makePrice } from '../../utils/Lots'
import { limitTextLength, calculateLimitTextLength } from '../../utils'
import BackButton from '../BackButton/BackButton'
import WatchButton from '../WatchButton/WatchButtonContainer'
import ImageCarousel from '../ImageCarousel/ImageCarousel'
import LotBottomInfo from '../LotBottomInfo/LotBottomInfo'
import DownloadCatalogueBtn from '../DownloadCatalogueBtn/DownloadCatalogueBtn'
import PrintCatalogueBtn from '../PrintCatalogueBtn/PrintCatalogueBtn'
import EmailModalOfferBoard from '../Modals/EmailModalOfferBoardContainer'

/*
 * OfferBoardCatalogue
 *
 * Every Lot listed on an Offer Board, read in one page the way an auction
 * catalogue reads an auction. A board never runs at a set time, so a listing
 * carries its Sell Price and the days it has left instead of an auction date.
 *
 * It is the easy way to see all the Lot Details, and a Buyer emails, downloads
 * or prints the whole catalogue from here.
 */
class OfferBoardCatalogue extends PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      offerBoard: null,
      lots: [],
      loaded: false,
      loadError: '',
      listType: 'list',
      modalIsOpen: false
    }
    this.openModal = this.openModal.bind(this)
    this.closeModal = this.closeModal.bind(this)
  }

  componentDidMount () {
    if (this.props.history.action !== 'POP') {
      window.scrollTo(0, 0)
    }
    this.loadCatalogue()
  }

  boardNumber () {
    return this.props.match.params.offerBoardNumber
  }

  async loadCatalogue () {
    this.props.showSpinner(true)
    // the whole board in one read - a catalogue is not paged, it is meant to be
    // looked through, printed or emailed in full
    let res = await Api.getOfferBoardLots(this.boardNumber())
    this.props.showSpinner(false)
    if (!res || !res.offerBoard) {
      this.setState({
        loaded: true,
        loadError: 'This Offer Board could not be found.'
      })
      return
    }
    this.setState({
      offerBoard: res.offerBoard,
      lots: res.lots || [],
      loaded: true
    })
  }

  openModal () {
    this.setState({ modalIsOpen: true })
  }

  closeModal () {
    this.setState({ modalIsOpen: false })
  }

  enterOfferBoard = () => {
    window.open(`#/offer-board/${this.state.offerBoard.searchNumber}`)
  }

  changeListType = (listType) => {
    this.setState({ listType })
  }

  // A board Lot is read on the same page an auction Lot is read on - the page
  // takes the board from the Lot itself.
  lotDetailsLink (lot) {
    return `/main/upcoming/${lot.kindData.title}/${this.state.offerBoard.searchNumber}/${lot.searchNumericId}`
  }

  lotLocation (lot) {
    let location = (lot.publicDetails && lot.publicDetails.location) || {}
    return ['town', 'state']
      .map(part => location[part])
      .filter(Boolean)
      .join(', ')
  }

  // What the Seller asks for the Lot. There is no Reserve Price on the board,
  // the Start Price is the Sell Price.
  sellPrice (lot) {
    let price = makePrice(lot.startPrice, { bidding: lot.bidding })
    if (!price) {
      return ''
    }
    return `${price}${lot.bidding === 'kg' ? '/kg' : '/head'}`
  }

  renderLot (lot) {
    let comment = (lot.publicDetails && lot.publicDetails.agentComment) || ''
    let readMore = comment.length > calculateLimitTextLength(comment, 420) ||
      comment.split(/\r\n|\r|\n/g).length >= 4
    return (
      <div className='lot-item' key={lot._id}>
        <div className='lot-img-container'>
          <ImageCarousel
            lot={lot}
            key={this.state.listType}
            lotId={lot._id}
            media={lot.media}
            kindType={lot.kindData && lot.kindData.type}
            preview />
        </div>
        <div className='lot-info'>
          <div className='lot-info-top'>
            <p className='lot-number'>
              {lot.number ? `Lot ${lot.number}` : lot.searchNumericId}
              {this.props.userId && String(lot.createdBy) === String(this.props.userId) &&
                <span className='my-listing'>
                  -
                  <span className='icon-wrapper'>
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <span className='my-listing-text'>My Listing</span>
                </span>
              }
            </p>
            <p className='lot-location'>{this.lotLocation(lot)}</p>
          </div>
          <div className='lot-info-middle'>
            <div className='left'>
              <p className='lot-title'>
                {lot.publicDetails.title} {lot.publicDetails.summary.age} {lot.additionalTitle}
              </p>
              <p className='lot-comments'>
                {limitTextLength(comment, calculateLimitTextLength(comment, 420), false)}
                {readMore &&
                  <NavLink className='lot-readMore' to={this.lotDetailsLink(lot)}>...Read More</NavLink>
                }
              </p>
            </div>
            <div className='info-btns'>
              <WatchButton lotId={lot._id} bigButton />
              <NavLink className='btn' to={this.lotDetailsLink(lot)}>
                <span>View Details</span>
                <span className='icon-wrapper icon-warning'><FontAwesomeIcon icon={faSearch} /></span>
              </NavLink>
            </div>
          </div>
          <div className='lot-info-bottom'>
            <div className='info-wrap'>
              <LotBottomInfo lot={lot} mode={this.state.listType} />
            </div>
            {/* the Sell Price the Seller asks and what the listing has left to
                run, one under the other so they never read as one line */}
            <div className='obc-listing'>
              <p className='obc-price'>{this.sellPrice(lot)}</p>
              {typeof lot.daysLeft === 'number' &&
                <p className='obc-days-left'>{lot.daysLeft} days left</p>
              }
            </div>
          </div>
        </div>
      </div>
    )
  }

  // The page sits in the same container the other screens sit in - it is what
  // holds the page off the edges of the window.
  renderPage (content) {
    return (
      <div className='app-route'>
        <div className='body-container'>
          <div className='inner-container'>
            {content}
          </div>
        </div>
      </div>
    )
  }

  render () {
    const { offerBoard, lots, loaded, loadError, listType } = this.state
    if (!loaded) {
      return <div />
    }
    if (!offerBoard) {
      return this.renderPage(
        <div className='lots-wrapper'><p className='fg-paragraph'>{loadError}</p></div>
      )
    }
    const summaryUrl = `${BASE_URL}print/offer-boards/${offerBoard.searchNumber}/catalogue-summary?type=pdf`
    const fullDetailsUrl = `${BASE_URL}print/offer-boards/${offerBoard.searchNumber}/full-details`
    return this.renderPage(
      <div className='lots-wrapper'>
        <EmailModalOfferBoard
          closeModal={this.closeModal}
          isOpen={this.state.modalIsOpen}
          offerBoard={offerBoard}
        />
        <div className='top-header'>
          <div className='left'>
            <h1 className='auction-title'>{offerBoard.title}</h1>
            <h2 className='auction-subtitle'>Offer Board No. {offerBoard.searchNumber}</h2>
            <p className='auction-live-date'>
              <span className='status status-open'>OFFERS ARE OPEN</span>
            </p>
            {(offerBoard.categories || []).map(category => (
              <p className='auction-live-date' key={category.kind}>
                {(category.kindData && category.kindData.title) || 'Category'}
                : listings expire after {category.listingExpiryDays} days
              </p>
            ))}
          </div>
        </div>

        <div className='top-btns upcoming'>
          <div className='left'>
            <BackButton onPress={() => { this.props.history.goBack() }} name='Back to Auctions' />
            <div className='btn' onClick={this.openModal}>
              <span>Email Catalogue</span>
              <span className='icon-wrapper icon-success'><FontAwesomeIcon icon={faEnvelope} /></span>
            </div>
            <DownloadCatalogueBtn
              summaryUrl={summaryUrl}
              fullDetailsUrl={fullDetailsUrl}
              auctionLotCount={lots.length}
            />
            <PrintCatalogueBtn
              summaryUrl={`${summaryUrl}&mode=print`}
              fullDetailsUrl={`${fullDetailsUrl}?type=pdf&mode=print`}
            />
          </div>
          <div className='right'>
            <div className='btn enter-auction-btn' onClick={this.enterOfferBoard}>
              <span>Enter Offer Board</span>
              <span className='icon-wrapper'><FontAwesomeIcon icon={faArrowRight} /></span>
            </div>
          </div>
        </div>

        <div className='catalog-settings'>
          <h2 className='catalog-header'>Catalogue</h2>
          <div className='view-modes'>
            <div className={`view-mode-button view-mode-list${listType === 'list' ? ' active' : ''}`}
              onClick={() => this.changeListType('list')} />
            <div className={`view-mode-button view-mode-grid${listType === 'grid' ? ' active' : ''}`}
              onClick={() => this.changeListType('grid')} />
          </div>
        </div>

        <div className='lots'>
          <div className='left-side'>
            <div className={listType === 'grid' ? 'lots-list grid' : 'lots-list list'}>
              {lots.map(lot => this.renderLot(lot))}
              {lots.length % 3 === 2 ? <div className='lot-item dump' key='dump' /> : null}
            </div>
          </div>
        </div>

        {!lots.length && (
          <div className='no-data-placeholder'>
            <p>There are no Lots on this Offer Board yet.</p>
          </div>
        )}
      </div>
    )
  }
}

OfferBoardCatalogue.propTypes = {
  showSpinner: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
}

export default OfferBoardCatalogue
